import { useCallback, useMemo, useState } from 'react';
import { IProgramWithPaymentCalculation, TMortgagePrograms } from '../../../types/mortgage';
import { useApplicationContext } from '../../../utils/applicationContext';
import { useMortgageFields } from './useMortgageFields';
import { useMortgagePrograms } from './useMortgagePrograms';
import { useEventCallback } from '@cian/react-utils';
import { useSelector } from 'react-redux';
import { selectProject } from '../../../selectors';
import { getInitialMortgageValues } from '../utils/getInitialMortgageValues';
import { useAnalytics } from './useAnalytics';

export const useMortgage = () => {
  const { startPrice } = useSelector(selectProject);
  const { config } = useApplicationContext();

  const buildingMortgagePrograms = useMemo(() => {
    const programs = config.get<TMortgagePrograms>('mortgageLanding.programs');

    return programs?.build;
  }, [config]);

  const initialValues = useMemo(
    () => getInitialMortgageValues({ startProjectPrice: startPrice, programs: buildingMortgagePrograms }),
    [buildingMortgagePrograms, startPrice],
  );

  const fields = useMortgageFields(initialValues);

  const { price, initialPayment, paymentPeriod } = fields;

  const renderedPrograms = useMortgagePrograms({
    price,
    initialPayment,
    paymentPeriod,
    programs: buildingMortgagePrograms,
  });

  const { trackMortgageProgramClick, ...analyticHandlers } = useAnalytics();

  const [selectedProgram, setSelectedProgram] = useState<IProgramWithPaymentCalculation | undefined>();

  const handleProgramClick = useCallback(
    (program: IProgramWithPaymentCalculation) => {
      setSelectedProgram(program);
      trackMortgageProgramClick(program.id);
    },
    [trackMortgageProgramClick],
  );

  const handleProgramModalClose = useEventCallback(() => {
    setSelectedProgram(undefined);
  });

  return useMemo(
    () => ({
      fields,
      programs: renderedPrograms,
      selectedProgram,
      resetSelectedProgram: handleProgramModalClose,
      selectProgram: handleProgramClick,
      ...analyticHandlers,
    }),
    [analyticHandlers, fields, handleProgramClick, handleProgramModalClose, renderedPrograms, selectedProgram],
  );
};
